import { absTimeDifference } from "utils/timeDifference";
import { VscPassFilled } from "react-icons/vsc";
import { MdPendingActions } from "react-icons/md";
import { TiTick, TiDelete } from "react-icons/ti";

export const leaveTypes = [
  {
    value: "RO",
    label: "Regular Off",
  },
  { value: "CL", label: "Compensation Leave" },
  { value: "NPL", label: "No Pay Leave" },
  { value: "AL", label: "Annual Leave" },
  { value: "SL", label: "Sick Leave" },
  { value: "BL", label: "Birthday Leave" },
  { value: "PL/ML", label: "Paternity Leave / Maternity Leave" },
  { value: "WFH", label: "Work From Home" },
];

export const amPmOptions = [
  { label: "AM", value: "am" },
  { label: "PM", value: "pm" },
];

export const isLeaveDeletionAllowed = (value) => {
  if (!value) return false;

  const [year, month, day] = String(value)
    .slice(0, 10)
    .split("-")
    .map(Number);
  const leaveDate = new Date(year, month - 1, day);
  const today = new Date();
  const cutoffDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 2,
  );

  return leaveDate > cutoffDate;
};

export const daysMapper = {
  RO: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
  ],
  CL: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
    { value: "PD", label: "Partial Day" },
  ],
  NPL: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
    { value: "PD", label: "Partial Day" },
  ],
  AL: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
  ],
  SL: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
  ],
  BL: [{ value: "FD", label: "Full Day" }],
  "PL/ML": [{ value: "FD", label: "Full Day" }],
  WFH: [
    { value: "HD", label: "Half Day" },
    { value: "FD", label: "Full Day" },
  ],
};

export const leaveTableColumns = [
  {
    id: "staffName",
    Header: "Staff Name",
    accessor: (row) => row.staff?.fullName,
    sortable: true,
    filterable: true,
  },
  {
    id: "status",
    Header: "Processed",
    accessor: (row) => (
      <div
        className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}
      >
        {row.status === "Processed" ? (
          <VscPassFilled size={20} />
        ) : (
          <MdPendingActions size={20} />
        )}
      </div>
    ),
    sortable: true,
    filterable: true,
  },
  {
    id: "date",
    Header: "Date",
    accessor: (row) => new Date(row.date).toISOString().split("T")[0],
    sortable: true,
    filterable: true,
  },
  {
    id: "leaveType",
    Header: "leaveType",
    accessor: "type",
    filterable: true,
    filter: (rows, columnIds, filterValue) => {
      if (filterValue === "all") return rows;
      return rows.filter((row) => row.values.leaveType === filterValue);
    },
    Filter: ({ setFilter, column }) => (
      <select
        className="form-control"
        onChange={(event) => setFilter(column.id, event.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="all">All</option>
        {leaveTypes.map(({ value, label }) => (
          <option value={value} key={value}>
            {label}
          </option>
        ))}
      </select>
    ),
  },
  {
    id: "approved",
    Header: "Approved",
    accessor: "approved",
    Cell: ({ value }) => (
      <div className={`${value ? "text-success" : "text-danger"}`}>
        {value ? <TiTick size={20} /> : <TiDelete size={20} />}
      </div>
    ),
    filter: (rows, columnIds, filterValue) => {
      if (filterValue === "all") return rows;
      return rows.filter(
        (row) => JSON.stringify(row.values.approved) === filterValue,
      );
    },
    Filter: ({ setFilter, column }) => (
      <select
        className="form-control"
        onChange={(event) => setFilter(column.id, event.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="all">All</option>
        <option value="true">Approved</option>
        <option value="false">Not Approved</option>
      </select>
    ),
    filterable: true,
  },
  {
    id: "duration",
    Header: "Duration",
    accessor: (row) => {
      if (row.period === "HD") return "4.00h";
      if (row.period === "FD") return "8.00h";
      return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
    },
    sortable: true,
    filterable: true,
  },
  {
    id: "actions",
    Header: "actions",
    accessor: "actions",
    sortable: false,
    filterable: false,
  },
];

export const otTableColumns = [
  {
    id: "staffName",
    Header: "Staff Name",
    accessor: (row) => row.staff?.fullName,
    sortable: true,
    filterable: true,
  },
  {
    id: "status",
    Header: "Status",
    accessor: (row) => (
      <div
        className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}
      >
        {row.status === "Processed" ? (
          <VscPassFilled size={20} />
        ) : (
          <MdPendingActions size={20} />
        )}
      </div>
    ),
    sortable: true,
    filterable: true,
  },
  {
    id: "date",
    Header: "Date",
    accessor: (row) => new Date(row.date).toDateString(),
    sortable: true,
    filterable: true,
  },
  {
    id: "duration",
    Header: "Duration",
    accessor: (row) => {
      if (row.period === "HD") return "4:00h";
      if (row.period === "FD") return "8:00h";
      return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
    },
    sortable: true,
    filterable: true,
  },
  {
    id: "approved",
    Header: "Approved",
    accessor: (row) => (
      <div className={`${row.approved ? "text-success" : "text-danger"}`}>
        {row.approved ? <TiTick size={20} /> : <TiDelete size={20} />}
      </div>
    ),
    sortable: true,
    filterable: true,
  },
  {
    id: "actions",
    Header: "actions",
    accessor: "actions",
    sortable: false,
    filterable: false,
  },
];

export const periodMap = {
  HD: "Half Day",
  FD: "Full Day",
  PD: "Partial Day",
};
